import { IconChevronDown } from '@leland-dev/leland-ui-library';
import { isEqual } from 'lodash-es';
import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { filterSearch } from '../../../utils/filterSearch';
import { useComponentBoundingBox } from '../../../utils/hooks/useComponentBoundingBox';
import { useDebounce } from '../../../utils/hooks/useDebounce';
import LoadingIndicator from '../../common/LoadingIndicator';
import { ErrorMessage } from '../../forms/Inputs';

import {
  type CommonSelectProps,
  createableLabel,
  DEFAULT_NO_OPTIONS_LABEL,
} from '.';

export type SelectProps<Value> = CommonSelectProps<Value> & {
  onChange: (value: Nullable<Value>) => void;
  value: Nullable<Value>;
  containerBoundingBox?: Possible<DOMRect>;
  defaultOption?: Value;
};

const Select = <Value,>({
  className = '',
  label,
  labelClassName = '',
  labelIsHidden,
  subLabel,
  flag,
  placeholder = 'Select an option...',
  options,
  isValueEqual = isEqual,
  loading,
  onChange,
  value,
  renderOption,
  error,
  disabled,
  // searchable props
  isSearchable,
  preventFilterSearch,
  onInputChange,
  createProps,
  required = false,
  customErrorMessage = required ? 'This is required' : undefined,
  containerBoundingBox,
  defaultOption,
}: SelectProps<Value>): ReactElement<SelectProps<Value>> => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [input, setInputDirect] = useState<string>('');
  const query = useDebounce(input, 100);
  const [optionsRef, optionsBoundingBox] =
    useComponentBoundingBox<HTMLUListElement>();

  const [validationError, setValidationError] = useState<boolean>(false);

  const setInput = useCallback(
    (value: string) => {
      setInputDirect(value);
      onInputChange?.(value);
    },
    [onInputChange],
  );

  const openInput = (e: React.MouseEvent<HTMLButtonElement>) => {
    // stop propagation and timeout is needed to ensure that the input is focused after the click event is handled
    e.stopPropagation();
    setIsOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    });
  };

  const closeInput = useCallback(() => {
    setIsOpen(false);
    setInput('');
    inputRef.current?.blur();
  }, [setInput]);

  const selectedOption = useMemo(() => {
    if (value) {
      return options.find((opt) => isValueEqual(opt.value, value));
    }
  }, [options, value, isValueEqual]);

  const filteredOptions = useMemo(() => {
    if (!preventFilterSearch && query) {
      return filterSearch(query, options, ['label']);
    }
    return options;
  }, [query, options, preventFilterSearch]);

  useEffect(() => {
    if (isOpen) {
      const abortController = new AbortController();
      const { signal } = abortController;

      const handleClick = (e: MouseEvent) => {
        if (anchorRef.current?.contains(e.target as Node)) {
          return;
        }
        closeInput();
      };
      document.addEventListener('click', handleClick, { signal });

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          closeInput();
        }
      };
      document.addEventListener('keydown', handleKeyDown, { signal });

      return () => abortController.abort();
    }
  }, [closeInput, isOpen]);

  const maxHeight = useMemo(() => {
    if (!containerBoundingBox || !optionsBoundingBox) {
      return null;
    }
    return containerBoundingBox.bottom - optionsBoundingBox.top - 16;
  }, [containerBoundingBox, optionsBoundingBox]);

  return (
    <div className={`${className} relative`}>
      <div className="space-y-3">
        {!labelIsHidden ? (
          <label className={`space-y-1 text-lg leading-6 ${labelClassName}`}>
            <h3 className="text-leland-gray-dark">
              {label}
              {flag ? (
                <span className="ml-2 text-leland-gray-extra-light">
                  {flag}
                </span>
              ) : null}
            </h3>
            {subLabel ? (
              <p className="text-leland-gray-light">{subLabel}</p>
            ) : null}
          </label>
        ) : null}
        <div ref={anchorRef}>
          <button
            type="button"
            className={`input flex w-full cursor-pointer select-none items-center gap-1 rounded-lg border border-leland-gray-stroke bg-white p-4 text-left shadow-none transition duration-100 focus-within:border-leland-gray-extra-light focus-within:shadow-sm ${
              disabled && 'button--disabled bg-leland-gray-light'
            } ${isOpen ? 'border-leland-gray-extra-light shadow-sm' : ''}`}
            // if searchable, ensure all clicks on button focus the input instead of blurring it
            onClick={
              isOpen ? (isSearchable ? undefined : closeInput) : openInput
            }
            onKeyUp={(e) => {
              // prevent SPACE from closing input
              if (isOpen && e.key === ' ') {
                e.preventDefault();
              }
            }}
            disabled={disabled}
          >
            <div className="flex w-full flex-1 items-center">
              {selectedOption && !isOpen ? (
                renderOption ? (
                  renderOption(selectedOption.value)
                ) : (
                  <>
                    {selectedOption.labelIcon ?? null}
                    {selectedOption.label}
                  </>
                )
              ) : isOpen || !defaultOption || !renderOption ? (
                <input
                  ref={inputRef}
                  className={`w-full bg-transparent outline-none focus:outline-none ${
                    (!isOpen || !isSearchable) && 'cursor-pointer'
                  }`}
                  placeholder={placeholder}
                  value={input}
                  onChange={(e) => {
                    setValidationError(false);
                    setInput(e.currentTarget.value);
                  }}
                  readOnly={!isSearchable}
                  disabled={disabled}
                  required={required}
                  onInvalid={(e) => {
                    e.preventDefault();
                    setValidationError(true);
                  }}
                />
              ) : (
                renderOption(defaultOption)
              )}
            </div>
            <IconChevronDown className="size-4" />
          </button>
          {isOpen ? (
            <ul
              className="absolute top-full z-10 mt-2 max-h-60 min-h-14 w-full overflow-auto rounded-lg border border-leland-gray-stroke bg-white p-2 shadow-lg"
              ref={optionsRef}
              style={maxHeight ? { maxHeight } : {}}
            >
              {loading ? (
                <LoadingIndicator iconClassName="w-6 h-6" />
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <li key={option.label}>
                    <button
                      type="button"
                      disabled={option.disabled}
                      className={`flex w-full items-center rounded-md p-3 text-left hover:bg-leland-gray-hover focus:bg-leland-gray-hover ${
                        option.disabled ? 'button--disabled' : ''
                      }`}
                      onClick={() => {
                        setValidationError(false);
                        onChange(option.value);
                        closeInput();
                      }}
                    >
                      {renderOption?.(
                        option.value,
                        selectedOption
                          ? isValueEqual(selectedOption.value, option.value)
                          : false,
                      ) ?? (
                        <div>
                          <p className="text-lg text-leland-gray-dark">
                            {option.label}
                          </p>
                          {option.subLabel ? (
                            <p className="text-leland-gray-extra-light">
                              {option.subLabel}
                            </p>
                          ) : null}
                        </div>
                      )}
                    </button>
                  </li>
                ))
              ) : (
                <li
                  className={`flex w-full items-center rounded-md p-2 ${
                    createProps && input
                      ? ''
                      : 'justify-center text-leland-gray-light'
                  }`}
                >
                  {createProps && input ? (
                    <button
                      type="button"
                      className="w-full rounded-md p-3 text-left hover:bg-leland-gray-hover focus:bg-leland-gray-hover"
                      onClick={async () => {
                        const value = await createProps.onCreate(input);
                        onChange(value);
                        closeInput();
                      }}
                    >
                      {createableLabel(input, createProps.itemType)}
                    </button>
                  ) : (
                    DEFAULT_NO_OPTIONS_LABEL
                  )}
                </li>
              )}
            </ul>
          ) : null}
        </div>
      </div>

      {(error?.message ?? validationError) ? (
        <ErrorMessage
          errorMessage={error?.message ?? customErrorMessage ?? ''}
        />
      ) : null}
    </div>
  );
};

export default Select;
